import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UsersApi } from '@api/users/users.api';
import { environment } from '@environment';
import { AccountPreferences, CognitoResponse, User } from '@interfaces';
import { Apollo } from 'apollo-angular';
import { chain, isArray } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ExpiredPasswordError } from './exceptions/expired-password-error.exception';
import { ExpiredPasswordSoonError } from './exceptions/expired-password-soon-error.exception';
import { NewPasswordRequiredError } from './exceptions/password-required-error.exception';
import { SoftwareTokenMfaRequiredError } from './exceptions/software-token-mfa-qequired-error.exception';
import { UnauthorizedUserError } from './exceptions/unauthorized-user-error.exception';

type LoginProvider = 'active-directory' | 'email-password' | 'google';

interface LoginParams {
  provider: LoginProvider;
  email?: string;
  password?: string;
  adIdToken?: string;
  accessToken?: string;
}

interface LoginState {
  user: User;
  permissions: string[];
  accountPreferences: AccountPreferences;
  idToken: string;
  accessToken: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private session: string;
  private resetPasswordSession: string;
  public user$: Observable<User>;
  public state: BehaviorSubject<LoginState>;
  private refreshTokenTimeoutId: any;

  constructor(
    private http: HttpClient,
    private usersApi: UsersApi,
    private readonly apolo: Apollo
  ) {
    this.state = new BehaviorSubject<LoginState>(null);
    this.user$ = this.state
      .asObservable()
      .pipe(map((state) => state && state.user));
  }

  public async getMe({ accessToken, updateState }: { accessToken?: string, updateState: boolean }): Promise<{
    me: User,
    permissions: string[],
    accountPreferences: AccountPreferences
  }> {
    const token = accessToken || this.accessToken;
    const { me, accountPreferences } = await this.usersApi.meWithAccessToken(token);

    const permissions = chain((me && me.groups) || [])
      .map((group) => group.permissions)
      .flatten()
      .map((permission) => permission.name)
      .uniq()
      .toArray()
      .value();

    const isAdminOrAll = me && ['admin', 'all'].includes(me.type);
    const hasSomePermission = permissions.length > 0;
    if (!isAdminOrAll || !hasSomePermission) {
      await this.logout();
      throw new UnauthorizedUserError();
    }

    if (updateState) {
      this.state.next({
        user: me,
        permissions,
        accountPreferences,
        idToken: this.idToken,
        accessToken: this.accessToken,
      });
    }

    return { me, accountPreferences, permissions };
  }

  public get user(): User {
    return this.state.value && this.state.value.user;
  }

  public get accessToken(): string {
    return this.state.value && this.state.value.accessToken;
  }

  public get idToken(): string {
    return this.state.value && this.state.value.idToken;
  }

  public get permissions(): string[] {
    return (this.state.value && this.state.value.permissions) || [];
  }

  public get preferences(): AccountPreferences {
    return (this.state.value && this.state.value.accountPreferences) || {};
  }

  private getEndpoint(provider: string): string {
    if (provider === 'email-password') {
      return 'sign-in';
    }

    if (provider === 'active-directory') {
      return 'sign-in-with-ad';
    }

    if (provider === 'google') {
      return 'sign-in-with-google';
    }

    return null;
  }


  public async login({ email, adIdToken, password, provider }: LoginParams): Promise<CognitoResponse | User> {

    const endpoint = await this.getEndpoint(provider);

    const body = provider === 'email-password' ? { email, password } : { idToken: adIdToken };
    const url = `${environment.MORANNON_URL}/auth/${endpoint}`;
    const response = await this.http.post<CognitoResponse>(url, body).toPromise();

    if (response.session) {
      this.session = response.session;
    }

    if (response.resetPasswordSession) {
      this.resetPasswordSession = response.resetPasswordSession;
    }

    if (response.passwordStatus && response.passwordStatus.isPasswordExpired) {
      throw new ExpiredPasswordError();
    }

    if (response.passwordStatus && response.passwordStatus.isPasswordSoonExpired) {
      throw new ExpiredPasswordSoonError(response);
    }

    if (response.challengeName === 'NEW_PASSWORD_REQUIRED') {
      throw new NewPasswordRequiredError();
    }

    if (response.challengeName === 'SOFTWARE_TOKEN_MFA') {
      throw new SoftwareTokenMfaRequiredError();
    }

    return this.updateStateAndSetRefreshToken(response);
  }

  public async loginWithSoftwareToken({ email, code }): Promise<User> {
    const url = `${environment.MORANNON_URL}/auth/respond-challenge`;
    const body = {
      session: this.session,
      challengeName: 'SOFTWARE_TOKEN_MFA',
      challengeResponses: {
        SOFTWARE_TOKEN_MFA_CODE: code,
        USERNAME: email
      }
    };

    const response = await this.http.post<CognitoResponse>(url, body).toPromise();
    return this.updateStateAndSetRefreshToken(response);
  }

  public async loginWithRecoveryCode({ email, password, recoveryCode }): Promise<User> {
    const url = `${environment.MORANNON_URL}/auth/sign-in-with-recovery-code`;
    const body = { email, password, recoveryCode };

    const response = await this.http.post<CognitoResponse>(url, body).toPromise();
    return this.updateStateAndSetRefreshToken(response);
  }

  async logout(): Promise<void> {
    const accessToken = this.accessToken;
    if (accessToken) {
      try {
        await this.http.post(`${environment.MORANNON_URL}/auth/logout`, { accessToken }).toPromise();
      } catch (ignored) {}
    }

    this.state.next(null);
    localStorage.removeItem('REFRESH_TOKEN');
    localStorage.removeItem('ACCESS_TOKEN');

    this.stopRefreshTokenTimer();
    try {
      await this.apolo.getClient().cache.reset();
    } catch (ignored) {
    }
  }

  async refreshTokenAndPermissions(): Promise<void> {
    const url = `${environment.MORANNON_URL}/auth/refresh-token`;

    const refreshToken = localStorage.getItem('REFRESH_TOKEN');
    if (!refreshToken) {
      return;
    }

    const response = await this.http.post<CognitoResponse>(url, { refreshToken })
      .toPromise()
      .catch(async () => {
        await this.logout();
        return null;
      });

    if (!response) {
      return;
    }

    const meResponse = await this.getMe({
      accessToken: response.accessToken,
      updateState: false
    }).catch(() => null);

    if (!meResponse) {
      return;
    }

    this.state.next({
      user: meResponse.me,
      permissions: meResponse.permissions,
      accountPreferences: meResponse.accountPreferences,
      idToken: response.idToken,
      accessToken: response.accessToken,
    });
    localStorage.setItem('ACCESS_TOKEN', response.accessToken);

    this.startRefreshTokenTimer();
  }

  private startRefreshTokenTimer(): void {
    const oneHour = 60 * 60 * 1000;
    clearTimeout(this.refreshTokenTimeoutId);
    this.refreshTokenTimeoutId = setTimeout(() => this.refreshTokenAndPermissions(), oneHour);
  }

  private stopRefreshTokenTimer(): void {
    clearTimeout(this.refreshTokenTimeoutId);
  }

  public async updateStateAndSetRefreshToken(response: CognitoResponse): Promise<User> {
    const { me: user, accountPreferences, permissions } = await this.getMe({ accessToken: response.accessToken, updateState: false });
    this.state.next({
      user,
      permissions,
      accountPreferences,
      idToken: response.idToken,
      accessToken: response.accessToken,
    });

    localStorage.setItem('REFRESH_TOKEN', response.refreshToken);
    localStorage.setItem('ACCESS_TOKEN', response.accessToken);
    this.startRefreshTokenTimer();

    return user;
  }

  async createNewPassword({ email, newPassword }): Promise<void> {
    const response = await this.http
      .post<CognitoResponse>(environment.MORANNON_URL + '/auth/confirm-registration', {
        email,
        newPassword,
        session: this.session
      })
      .toPromise();

    if (response.session) {
      this.session = response.session;
    }

    if (response.challengeName === 'SOFTWARE_TOKEN_MFA') {
      throw new SoftwareTokenMfaRequiredError();
    }
  }

  async createNewPasswordAfterExpiredAndLogin(newPassword: string): Promise<CognitoResponse> {
    const response = await this.http
      .post<CognitoResponse>(environment.MORANNON_URL + '/auth/post-auth-sign-in-with-new-password', {
        newPassword,
        resetPasswordSession: this.resetPasswordSession
      })
      .toPromise();

    if (response.session) {
      this.session = response.session;
    }

    if (response.challengeName === 'SOFTWARE_TOKEN_MFA') {
      throw new SoftwareTokenMfaRequiredError();
    }

    return response;
  }

  async sendPasswordRecoveryEmail(email: string): Promise<void> {
    const url = `${environment.MORANNON_URL}/auth/forgot-password`;
    await this.http.post(url, { email }).toPromise();
  }

  async confirmCodeAndCreateNewPassword({ email, verificationCode, newPassword }): Promise<any> {
    const url = `${environment.MORANNON_URL}/auth/confirm-password`;
    const response = await this.http
      .post<CognitoResponse>(url, {
        email: email,
        verificationCode: verificationCode,
        newPassword: newPassword
      })
      .toPromise();

    if (response.session) {
      this.session = response.session;
    }

    if (response.challengeName === 'SOFTWARE_TOKEN_MFA') {
      throw new SoftwareTokenMfaRequiredError();
    }

    return response;
  }

  public hasPermission(permissionName: string): boolean {
    return this.permissions.includes(permissionName);
  }

  public hasFlags(flags: string | string[]): boolean {
    if (!this.user || !this.user.account || !this.user.account.id) {
      return false;
    }

    return this.user.account.featureFlags.some((flag) => {
      return flag.active && (
        isArray(flags) ? flags.includes(flag.name) : flag.name === flags
      );
    });
  }

  public hasAccountPreference({ property, subProperty, expectedValue, defaultIfUndefined }): boolean {
    const expectedIsDefaultValue = defaultIfUndefined === expectedValue;
    const { accountPreferences } = this.state.value;

    if (!accountPreferences || !accountPreferences[property] || accountPreferences[property][subProperty] === null) {
      return expectedIsDefaultValue;
    }

    return accountPreferences[property][subProperty] === expectedValue;
  }

  public getAccountPreference({ property, subProperty, defaultIfNull }): string {
    const accountPreferences = this.state.value && this.state.value.accountPreferences;
    if (!accountPreferences || !accountPreferences[property] || accountPreferences[property][subProperty] === null) {
      return defaultIfNull;
    }

    return accountPreferences[property][subProperty];
  }
}
